import { useState, useEffect, useRef, useCallback } from 'react';
import { Warehouse, Package, Volume2, VolumeX, ArrowRight, User, MapPin, Bell } from 'lucide-react';
import axios from 'axios';
import { gorunurAralik } from '../utils/gorunurAralik';

const API = process.env.REACT_APP_BACKEND_URL;
const NOTIFICATION_SOUND = '/sounds/notification.wav';  // asıl bildirim sesimiz (alert.wav = bip)
const CHECK_INTERVAL = 15000; // görünür sekmede 15 sn; arka planda 60 sn (gorunurAralik)
const DISMISSED_KEY = 'panel_notif_dismissed_ids'; // Tüm sekmeler için ortak dismiss hafızası
const DISMISSED_MAX = 200; // Son N bildirim ID'sini tutarız
const BC_NAME = 'panel-notifications'; // BroadcastChannel adı

// Dismiss edilmiş bildirim ID'lerini localStorage'dan oku (tüm sekmeler paylaşır)
const getDismissedIds = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'));
  } catch { return new Set(); }
};
const addDismissedId = (id) => {
  try {
    const arr = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    if (arr.includes(id)) return;
    const next = [id, ...arr].slice(0, DISMISSED_MAX);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
  } catch {}
};

// Sekmenin aktif (kullanıcının bulunduğu) olup olmadığı — ses/modal yalnızca burada
const isTabActive = () => {
  if (typeof document === 'undefined') return true;
  // Görünür olması yeterli; hasFocus çoklu monitör/odak kaybında yanlış olumsuz verir
  return document.visibilityState === 'visible';
};

const PanelNotification = ({ firma }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(null); // 'siparis' | 'transfer'
  const [alertData, setAlertData] = useState(null);
  const [alertNotifId, setAlertNotifId] = useState(null); // Aktif bildirimin benzersiz ID'si
  const [sesAcik, setSesAcik] = useState(true);
  const [lastSiparisCount, setLastSiparisCount] = useState(null);
  const [lastTransferCount, setLastTransferCount] = useState(null);
  const [lastOnSiparisCount, setLastOnSiparisCount] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);

  const audioRef = useRef(null);
  const alertIntervalRef = useRef(null);
  const flashIntervalRef = useRef(null);
  const bcRef = useRef(null);

  // Audio init + BroadcastChannel (sekmeler arası iletişim)
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.8;

    // Sekmeler arası iletişim kanalı
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        bcRef.current = new BroadcastChannel(BC_NAME);
        bcRef.current.onmessage = (ev) => {
          const { type, id } = ev.data || {};
          if (!id) return;
          if (type === 'dismiss' || type === 'shown') {
            // Başka bir sekme bildirimi gösterdi/kapattı → bu sekmede de kapat
            addDismissedId(id);
            setShowAlert((prev) => {
              // Kapat yalnız aynı id gösteriliyorsa — aksi halde beklemedeki başka modal etkilenmez
              if (prev) {
                setAlertNotifId((curId) => {
                  if (curId === id) return null;
                  return curId;
                });
                return false;
              }
              return prev;
            });
          }
        };
      } catch { /* noop */ }
    }

    return () => {
      if (alertIntervalRef.current) clearInterval(alertIntervalRef.current);
      if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
      try { bcRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  const playAlertSound = useCallback(() => {
    if (sesAcik && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [sesAcik]);

  // Alert sound + flash interval
  useEffect(() => {
    if (showAlert) {
      // Ses
      if (sesAcik) {
        playAlertSound();
        alertIntervalRef.current = setInterval(playAlertSound, 15000);
      }
      
      // Yanıp sönme efekti
      flashIntervalRef.current = setInterval(() => {
        setIsFlashing(prev => !prev);
      }, 500);
    } else {
      if (alertIntervalRef.current) {
        clearInterval(alertIntervalRef.current);
        alertIntervalRef.current = null;
      }
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
        flashIntervalRef.current = null;
      }
      setIsFlashing(false);
    }
    return () => {
      if (alertIntervalRef.current) clearInterval(alertIntervalRef.current);
      if (flashIntervalRef.current) clearInterval(flashIntervalRef.current);
    };
  }, [showAlert, sesAcik, playAlertSound]);

  // Tek endpoint ile kontrol - 10 saniyede bir
  useEffect(() => {
    if (!firma) return;

    const checkNotifications = async () => {
      try {
        const response = await axios.get(`${API}/api/wms/bildirim-kontrol/${firma}`);
        const { siparis_sayisi, transfer_sayisi, siparisler, transferler, on_siparis_sayisi, on_siparisler } = response.data;

        // Yeni sipariş geldi mi?
        if (lastSiparisCount !== null && siparis_sayisi > lastSiparisCount) {
          const yeniSiparis = siparisler[0];
          // Benzersiz bildirim ID'si — sekmeler arası dedup için
          const notifId = `siparis:${yeniSiparis?.siparis_no || yeniSiparis?.order_no || yeniSiparis?._id || siparis_sayisi}`;
          const dismissed = getDismissedIds();
          const tabActive = isTabActive();

          if (dismissed.has(notifId)) {
            // Başka sekmede zaten kapatıldı → gösterme
          } else if (!tabActive) {
            // Kullanıcı bu sekmede değil → ses/modal gösterme, diğer aktif sekme gösterecek
          } else {
            // AKTİF SEKMEDEYİZ → göster + broadcast et ki diğer sekmeler de atsın
            addDismissedId(notifId); // kendi localStorage'ımızda da işaretle
            try { bcRef.current?.postMessage({ type: 'shown', id: notifId }); } catch {}

            // Kullanıcı bilgisini düzgün al
            let kullanici_adi = 'Bilinmiyor';
            if (yeniSiparis?.olusturan?.ad) {
              kullanici_adi = yeniSiparis.olusturan.ad;
            } else if (yeniSiparis?.olusturan?.email) {
              kullanici_adi = yeniSiparis.olusturan.email;
            } else if (typeof yeniSiparis?.olusturan === 'string') {
              kullanici_adi = yeniSiparis.olusturan;
            } else if (yeniSiparis?.created_by?.user_name) {
              kullanici_adi = yeniSiparis.created_by.user_name;
            } else if (yeniSiparis?.kullanici) {
              kullanici_adi = yeniSiparis.kullanici;
            }

            setAlertType('siparis');
            setAlertData({
              baslik: 'YENİ SİPARİŞ',
              count: siparis_sayisi - lastSiparisCount,
              detay: yeniSiparis ? {
                siparis_no: yeniSiparis.siparis_no || yeniSiparis.order_no,
                musteri: yeniSiparis.musteri_bilgileri?.ad || yeniSiparis.musteri_adi || yeniSiparis.hedef,
                musteri_adres: yeniSiparis.musteri_bilgileri?.adres || yeniSiparis.teslimat_adresi,
                tutar: yeniSiparis.toplam_tutar || yeniSiparis.toplam,
                kullanici: kullanici_adi,
                firma: yeniSiparis.firma || yeniSiparis.kaynak,
                tarih: yeniSiparis.created_at,
                urunler: yeniSiparis.urunler || yeniSiparis.kalemler
              } : null
            });
            setAlertNotifId(notifId);
            setShowAlert(true);
          }
        }

        // Yeni transfer isteği geldi mi?
        if (lastTransferCount !== null && transfer_sayisi > lastTransferCount && !showAlert) {
          const yeniTransfer = transferler[0];
          const notifId = `transfer:${yeniTransfer?.siparis_no || yeniTransfer?.transfer_no || yeniTransfer?.liste_no || yeniTransfer?._id || transfer_sayisi}`;
          const dismissed = getDismissedIds();
          const tabActive = isTabActive();

          if (dismissed.has(notifId)) {
            // Başka sekmede zaten kapatıldı
          } else if (!tabActive) {
            // Aktif değilim — diğer sekme halletsin
          } else {
            addDismissedId(notifId);
            try { bcRef.current?.postMessage({ type: 'shown', id: notifId }); } catch {}

            // Kullanıcı bilgisini düzgün al
            let kullanici_adi = 'Bilinmiyor';
            if (yeniTransfer?.olusturan?.ad) {
              kullanici_adi = yeniTransfer.olusturan.ad;
            } else if (yeniTransfer?.olusturan?.email) {
              kullanici_adi = yeniTransfer.olusturan.email;
            } else if (typeof yeniTransfer?.olusturan === 'string') {
              kullanici_adi = yeniTransfer.olusturan;
            } else if (yeniTransfer?.created_by?.user_name) {
              kullanici_adi = yeniTransfer.created_by.user_name;
            } else if (yeniTransfer?.kullanici) {
              kullanici_adi = yeniTransfer.kullanici;
            }

            // Müşteri bilgisini düzgün al (son_musteri veya musteri_bilgileri)
            const musteri_ad = yeniTransfer?.son_musteri?.musteri_adi ||
                              yeniTransfer?.son_musteri?.ad ||
                              yeniTransfer?.musteri_bilgileri?.ad ||
                              yeniTransfer?.musteri_adi || '';

            const musteri_adres = yeniTransfer?.son_musteri?.tam_adres ||
                                 yeniTransfer?.son_musteri?.musteri_adres ||
                                 yeniTransfer?.musteri_bilgileri?.adres ||
                                 yeniTransfer?.teslimat_adresi || '';

            setAlertType('transfer');
            setAlertData({
              baslik: 'TRANSFER İSTEĞİ',
              count: transfer_sayisi - lastTransferCount,
              detay: yeniTransfer ? {
                transfer_no: yeniTransfer.siparis_no || yeniTransfer.transfer_no || yeniTransfer.liste_no,
                siparis_no: yeniTransfer.ana_siparis_no || yeniTransfer.parent_siparis_no,
                kaynak: yeniTransfer.kaynak || yeniTransfer.stok_kaynak || yeniTransfer.hedef_firma || firma,
                hedef: yeniTransfer.hedef || yeniTransfer.firma || yeniTransfer.talep_eden_firma,
                isteyen_firma: yeniTransfer.parent_firma || yeniTransfer.hedef || yeniTransfer.firma || yeniTransfer.talep_eden_firma,
                musteri: musteri_ad,
                musteri_adres: musteri_adres,
                kullanici: kullanici_adi,
                is_musteri_siparisi: !!musteri_ad,
                urunler: yeniTransfer.urunler || yeniTransfer.kalemler,
                tarih: yeniTransfer.created_at
              } : null
            });
            setAlertNotifId(notifId);
            setShowAlert(true);
          }
        }

        // Yeni ön sipariş geldi mi?
        if (lastOnSiparisCount !== null && on_siparis_sayisi > lastOnSiparisCount && !showAlert) {
          const yeniOn = (on_siparisler || [])[0];
          const notifId = `onsiparis:${yeniOn?.id || on_siparis_sayisi}`;
          const dismissed = getDismissedIds();
          const tabActive = isTabActive();
          if (dismissed.has(notifId)) {
            // başka sekmede zaten kapatıldı
          } else if (!tabActive) {
            // aktif sekme değil — diğer sekme gösterir
          } else {
            addDismissedId(notifId);
            try { bcRef.current?.postMessage({ type: 'shown', id: notifId }); } catch {}
            setAlertType('on_siparis');
            setAlertData({
              baslik: 'YENİ ÖN SİPARİŞ',
              count: on_siparis_sayisi - lastOnSiparisCount,
              detay: yeniOn ? {
                siparis_no: yeniOn.id,
                musteri: yeniOn.musteri_ad || yeniOn.musteri_email,
                kullanici: yeniOn.musteri_email,
                urunler: [{ urun_adi: yeniOn.urun_adi, sku: yeniOn.sku, miktar: yeniOn.miktar }]
              } : null
            });
            setAlertNotifId(notifId);
            setShowAlert(true);
          }
        }

        setLastSiparisCount(siparis_sayisi);
        setLastTransferCount(transfer_sayisi);
        setLastOnSiparisCount(on_siparis_sayisi);

      } catch (error) {
        // Sessizce hata yönet
      }
    };

    checkNotifications();
    // Sesli uyarı verdiği için arka plandaki sekmede de sorulur, ama dakikada bir (2026-10-20)
    return gorunurAralik(checkNotifications, CHECK_INTERVAL, 60000);
  }, [firma, lastSiparisCount, lastTransferCount, lastOnSiparisCount, showAlert]);

  const handleDismiss = () => {
    // Bu bildirimi tüm sekmelerde kapat
    if (alertNotifId) {
      addDismissedId(alertNotifId);
      try { bcRef.current?.postMessage({ type: 'dismiss', id: alertNotifId }); } catch {}
    }
    setShowAlert(false);
    setAlertNotifId(null);
  };

  if (!showAlert) return null;

  const detay = alertData?.detay;

  // Açık mavi / antrasit tema + yanıp sönme
  return (
    <>
      {/* Overlay - Yanıp sönen */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
          isFlashing ? 'bg-slate-900/80' : 'bg-slate-800/70'
        }`}
        onClick={handleDismiss}
      >
        {/* Modal - Açık mavi/antrasit tema */}
        <div 
          className={`relative w-full max-w-2xl mx-4 rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
            isFlashing 
              ? 'scale-105 shadow-cyan-500/50' 
              : 'scale-100'
          }`}
          style={{
            background: (alertType === 'siparis' || alertType === 'on_siparis')
              ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%)' // Açık mavi
              : 'linear-gradient(135deg, #64748b 0%, #475569 50%, #334155 100%)' // Açık antrasit
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Yanıp sönen border efekti */}
          <div className={`absolute inset-0 rounded-3xl transition-all duration-300 ${
            isFlashing 
              ? 'ring-4 ring-white/60 ring-offset-2 ring-offset-transparent' 
              : 'ring-2 ring-white/20'
          }`} />
          
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className={`absolute inset-0 transition-opacity duration-500 ${
              isFlashing ? 'opacity-30' : 'opacity-10'
            }`} style={{
              backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
              backgroundSize: '30px 30px'
            }} />
          </div>
          
          {/* Content */}
          <div className="relative p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-2xl transition-all duration-300 ${
                  isFlashing ? 'bg-white/30 scale-110' : 'bg-white/20'
                }`}>
                  {(alertType === 'siparis' || alertType === 'on_siparis') ? (
                    <Package size={48} className="text-white" />
                  ) : (
                    <Warehouse size={48} className="text-white" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <Bell className={`text-yellow-300 ${isFlashing ? 'animate-bounce' : ''}`} size={28} />
                    <h2 className="text-3xl font-black text-white tracking-wide">
                      {alertData?.baslik}
                    </h2>
                  </div>
                  <p className="text-white/90 text-lg mt-1">
                    {alertData?.count} yeni {alertType === 'siparis' ? 'sipariş' : alertType === 'on_siparis' ? 'ön sipariş' : 'transfer isteği'}
                  </p>
                </div>
              </div>
              
              {/* Sound Toggle */}
              <button
                onClick={() => setSesAcik(!sesAcik)}
                className={`p-3 rounded-xl transition-all ${
                  sesAcik ? 'bg-white/30 text-white' : 'bg-white/10 text-white/50'
                }`}
              >
                {sesAcik ? <Volume2 size={24} /> : <VolumeX size={24} />}
              </button>
            </div>

            {/* Detay Bilgileri */}
            {detay && (
              <div className={`backdrop-blur rounded-2xl p-6 mb-6 transition-all duration-300 ${
                isFlashing ? 'bg-white/20' : 'bg-white/10'
              }`}>
                {alertType === 'transfer' ? (
                  <>
                    {/* Transfer Detayları */}
                    {/* Transfer ve Sipariş No */}
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/30">
                      <div>
                        <p className="text-white/70 text-sm">Transfer No</p>
                        <p className="text-white font-bold text-lg">{detay.transfer_no || '-'}</p>
                      </div>
                      {detay.siparis_no && (
                        <div className="text-right">
                          <p className="text-white/70 text-sm">Sipariş No</p>
                          <p className="text-white font-bold text-lg">{detay.siparis_no}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Nereden Nereye */}
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="text-center">
                        <div className={`rounded-xl px-6 py-3 transition-all ${
                          isFlashing ? 'bg-red-500/40' : 'bg-red-500/30'
                        }`}>
                          <p className="text-white/70 text-xs">STOK ÇIKIŞ</p>
                          <p className="text-white font-bold text-xl">{detay.kaynak}</p>
                        </div>
                      </div>
                      <ArrowRight size={32} className={`text-white ${isFlashing ? 'animate-pulse' : ''}`} />
                      <div className="text-center">
                        <div className={`rounded-xl px-6 py-3 transition-all ${
                          isFlashing ? 'bg-green-500/40' : 'bg-green-500/30'
                        }`}>
                          <p className="text-white/70 text-xs">STOK GİRİŞ</p>
                          <p className="text-white font-bold text-xl">{detay.hedef}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Müşteri Bilgisi (varsa) */}
                    {detay.musteri && (
                      <div className="bg-cyan-500/20 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <User size={18} className="text-cyan-200" />
                          <span className="text-cyan-100 text-sm font-semibold">MÜŞTERİYE SEVKİYAT</span>
                        </div>
                        <p className="text-white font-bold text-lg">{detay.musteri}</p>
                        {detay.musteri_adres && (
                          <p className="text-white/70 text-sm mt-1">{detay.musteri_adres}</p>
                        )}
                      </div>
                    )}
                    
                    {!detay.musteri && (
                      <div className="bg-purple-500/20 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Warehouse size={18} className="text-purple-200" />
                          <span className="text-purple-100 font-semibold">DEPO TRANSFER</span>
                        </div>
                        <p className="text-white/70 text-sm mt-1">Stok {detay.hedef} deposuna transfer edilecek</p>
                      </div>
                    )}
                    
                    {/* İşlemi Giren */}
                    <div className="flex items-center justify-between text-white mb-3">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-white/70" />
                        <span className="text-white/70">İşlemi Giren:</span>
                      </div>
                      <span className="font-semibold">{detay.kullanici || 'Bilinmiyor'}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-white/70" />
                        <span className="text-white/70">İsteyen Firma:</span>
                      </div>
                      <span className="font-semibold">{detay.isteyen_firma}</span>
                    </div>
                    
                    {/* Ürünler */}
                    {detay.urunler && detay.urunler.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/20">
                        <p className="text-white/70 text-sm mb-2">İstenen Ürünler ({detay.urunler.length} kalem):</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {detay.urunler.slice(0, 5).map((urun, idx) => (
                            <div key={idx} className="flex justify-between text-white text-sm bg-white/10 rounded px-3 py-2">
                              <span className="truncate flex-1">{urun.urun_adi || urun.sku || urun.stok_kodu}</span>
                              <span className="font-bold ml-2">{urun.miktar || urun.adet} ad.</span>
                            </div>
                          ))}
                          {detay.urunler.length > 5 && (
                            <p className="text-white/60 text-sm text-center">+{detay.urunler.length - 5} ürün daha</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Sipariş Detayları */}
                    <div className="grid grid-cols-2 gap-4 text-white">
                      {detay.siparis_no && (
                        <div className="col-span-2 pb-3 border-b border-white/30">
                          <p className="text-white/70 text-sm">Sipariş No</p>
                          <p className="font-bold text-2xl">{detay.siparis_no}</p>
                        </div>
                      )}
                      {detay.musteri && (
                        <div className="col-span-2 bg-cyan-500/20 rounded-xl p-3">
                          <p className="text-white/70 text-sm">Müşteri</p>
                          <p className="font-bold text-lg">{detay.musteri}</p>
                          {detay.musteri_adres && (
                            <p className="text-white/60 text-sm mt-1">{detay.musteri_adres}</p>
                          )}
                        </div>
                      )}
                      {detay.tutar && (
                        <div>
                          <p className="text-white/70 text-sm">Toplam Tutar</p>
                          <p className="font-bold text-xl text-green-300">
                            {typeof detay.tutar === 'number' ? detay.tutar.toLocaleString('tr-TR') : detay.tutar} ₺
                          </p>
                        </div>
                      )}
                      {detay.firma && (
                        <div>
                          <p className="text-white/70 text-sm">Firma</p>
                          <p className="font-bold text-lg">{detay.firma}</p>
                        </div>
                      )}
                      {detay.kullanici && (
                        <div className="col-span-2 pt-3 border-t border-white/30">
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-white/70" />
                            <span className="text-white/70">İşlemi Giren:</span>
                            <span className="font-semibold">{detay.kullanici}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Ürünler */}
                    {detay.urunler && detay.urunler.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/20">
                        <p className="text-white/70 text-sm mb-2">Sipariş Kalemleri ({detay.urunler.length}):</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {detay.urunler.slice(0, 5).map((urun, idx) => (
                            <div key={idx} className="flex justify-between text-white text-sm bg-white/10 rounded px-3 py-2">
                              <span className="truncate flex-1">{urun.urun_adi || urun.sku}</span>
                              <span className="font-bold ml-2">{urun.miktar || urun.adet} ad.</span>
                            </div>
                          ))}
                          {detay.urunler.length > 5 && (
                            <p className="text-white/60 text-sm text-center">+{detay.urunler.length - 5} ürün daha</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Buton */}
            <button
              onClick={handleDismiss}
              className={`w-full py-4 rounded-2xl font-bold text-xl transition-all shadow-lg hover:shadow-xl active:scale-98 ${
                isFlashing 
                  ? 'bg-white text-slate-800 hover:bg-gray-100' 
                  : 'bg-white/90 text-slate-700 hover:bg-white'
              }`}
            >
              ✓ KAPAT
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PanelNotification;
